import { useState } from "react";

import pageLoading from "@assets/page-loading.gif";
import { CreateMediaPlanTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import { MMMTab } from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";
import { IconButton } from "@shared/buttons";

import { CreateBasicMediaPlanModal } from "./create-media-plan-modal/create-basic-media-plan-modal";
import { CreateMediaPlanModal } from "./create-media-plan-modal/create-media-plan-modal";
import { useSelectedMediaPlanModal } from "./create-media-plan-modal/use-selected-media-plan-modal";
import { useIsAutoblessedModelsEnabled } from "./hooks/use-is-autoblessed-models-enabled";
import { useMediaPlanData } from "./hooks/use-media-plan";
import { useMediaPlans } from "./hooks/use-media-plans";
import { MediaPlanTable } from "./media-plan-table";

export const MediaPlanTab = ({
  goBack,
  goBackText,
  autoOpenModal = false,
}: {
  goBack: () => void;
  goBackText: string;
  autoOpenModal?: boolean;
}) => {
  const areAutoblessedModelsEnabled = useIsAutoblessedModelsEnabled();
  return areAutoblessedModelsEnabled ? (
    <BasicMediaPlanTab
      goBack={goBack}
      goBackText={goBackText}
      autoOpenModal={autoOpenModal}
    />
  ) : (
    <DeprecatedMediaPlanTab />
  );
};

export const BasicMediaPlanTab = ({
  goBack,
  goBackText,
  autoOpenModal,
}: {
  goBack: () => void;
  goBackText: string;
  autoOpenModal: boolean;
}) => {
  const {
    mediaPlans,
    loadingAllPlans: isLoading,
    isRefetchingMediaPlans,
  } = useMediaPlans();

  const trackMMMEvent = useTrackMMMEvent(MMMTab.CREATE_MEDIA_PLAN);

  const [isModalOpen, setIsModalOpen] = useState(autoOpenModal);
  const [selectedMediaPlanId, setSelectedMediaPlanId] = useState<string | null>(
    null,
  );

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedMediaPlanId(null);
    trackMMMEvent({
      component: CreateMediaPlanTrackedComponent.CREATE_MEDIA_PLAN_MODAL,
      action: "onClose",
      actionInfo: "Close media plan modal",
    });
  };

  const openModalForMediaPlan = (mediaPlanId: string) => {
    setSelectedMediaPlanId(mediaPlanId);
    setIsModalOpen(true);
    trackMMMEvent({
      component: CreateMediaPlanTrackedComponent.CREATE_MEDIA_PLAN_MODAL,
      action: "onOpen",
      actionInfo: "Open media plan modal for existing media plan",
    });
  };

  if (isLoading) {
    return (
      <img
        src={pageLoading}
        alt="Chart loading gif"
        className="w-1/2 m-auto pt-20"
      />
    );
  }

  return (
    <div className="relative">
      {isRefetchingMediaPlans && (
        <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50">
          <img
            src={pageLoading}
            alt="Chart loading gif"
            className="w-32 opacity-70"
          />
        </div>
      )}
      <div className="flex justify-between bg-v2-action-tertiary my-4 rounded-lg">
        <IconButton
          variant="secondary"
          onClick={goBack}
          leftIcon={<i className="fa-regular fa-sharp fa-arrow-left" />}
          className="mr-3 my-2"
        >
          {goBackText ?? "VIEW CURRENT MEDIA PLAN"}
        </IconButton>
        <IconButton
          variant="secondary"
          leftIcon={<i className="fa-regular fa-sharp fa-plus" />}
          className="mr-3 my-2"
          onClick={() => {
            setIsModalOpen(true);
            trackMMMEvent({
              component:
                CreateMediaPlanTrackedComponent.CREATE_MEDIA_PLAN_MODAL,
              action: "onOpen",
              actionInfo: "Choose to create new media plan (open modal)",
            });
          }}
        >
          CREATE NEW PLAN
        </IconButton>
      </div>
      <MediaPlanTable
        mediaPlanList={mediaPlans}
        onSelect={openModalForMediaPlan}
      />
      <CreateBasicMediaPlanModal
        isOpen={isModalOpen}
        onClose={closeModal}
        selectedMediaPlanId={selectedMediaPlanId}
      />
    </div>
  );
};

/** @deprecated */
export const DeprecatedMediaPlanTab = () => {
  const { mediaPlanList, isLoading } = useMediaPlanData();
  const { isCreateMediaPlanModalOpen, closeCreateMediaPlanModal } =
    useSelectedMediaPlanModal();

  const { selectedMediaPlan, createNewMediaPlan } = useSelectedMediaPlanModal();

  if (isLoading) {
    return (
      <img
        src={pageLoading}
        alt="Chart loading gif"
        className="w-1/2 m-auto pt-20"
      />
    );
  }

  return (
    <div>
      <div className="flex justify-end bg-v2-action-tertiary my-4 rounded-lg">
        <IconButton
          variant="secondary"
          leftIcon={<i className="fa-regular fa-sharp fa-plus" />}
          className="mr-3 my-2"
          onClick={createNewMediaPlan}
        >
          CREATE NEW PLAN
        </IconButton>
      </div>
      <MediaPlanTable mediaPlanList={mediaPlanList} />
      {selectedMediaPlan && (
        // NOTE: Not fixing typing in deprecated component
        <CreateMediaPlanModal
          isOpen={isCreateMediaPlanModalOpen}
          onClose={closeCreateMediaPlanModal}
          selectedMediaPlan={selectedMediaPlan}
        />
      )}
    </div>
  );
};
